import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { addQuery } from 'Helpers/Query';
import { buildModalQuery } from 'Consts/ModalQueries';
import { withStyles } from '@material-ui/core/styles';
import modalConsts from 'Consts/ModalNames';
import { openModal } from 'Helpers/ModalHelper';
import Messages from 'Components/UI/Messages';
import Button from 'Components/UI/Button';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import Paper from '@material-ui/core/Paper';
import {
  Divider,
} from '@material-ui/core';
import { householdBlueprintUri, householdPlansUri } from 'Consts/advisorUris';
import ActionItemSelector from 'Components/UI/ActionItemSelector';
import PreviousEmail from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/PreviousEmails/PreviousEmail';
import Expandable from 'Components/UI/Expandable';
import { missionControlURL } from 'Hooks/useMissionControlURL';


const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: '90%',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  titleTop: {
    font: 'normal normal medium 20px/28px Euclid Square',
    fontWeight: theme.typography.fontWeightBold,
    padding: theme.spacing(0, 0, 2, 0),
    letterSpacing: 0,
  },
  actions: {
    display: 'flex',
  },
  actionItems: {
    flexShrink: 1,
    minWidth: theme.dimensions.sm,
  },
  messages: {
    flexGrow: 1,
    maxHeight: theme.dimensions.md,
    paddingLeft: theme.spacing(2),
    overflow: 'visible',
  },
  openMissionControl: {
    borderRadius: '0 0 4px 4px',
  },
  actionButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
});

function Meeting({
  className,
  classes,
  meetingResource,
  plannerActionItems,
  setPlannerActionItems,
  clientActionItems,
  setClientActionItems,
  meetingActionItems,
  setMeetingActionItems,
  onActionItemToggle,
}) {
  const handleClickActionItem = (ai) => {
    addQuery({ todoId: ai.id() });
    openModal(modalConsts.simpleActionItemModal);
  };

  const handleToggleActionItem = (ai, clientAssigned) => {
    if (meetingActionItems.includes(ai.id())) {
      ai.delete().then((coll) => {
        const filteredActionItems = meetingActionItems.filter(
          id => id !== coll.resources().head().id(),
        );
        onActionItemToggle(meetingResource.id(), false);
        setMeetingActionItems(filteredActionItems);
      }).catch((err) => {
        displayErrorMessage(err, err.errors);
      });
    } else {
      fr.http.Put(meetingResource.relationship('simpleActionItemLinks')
        .link('related')
        .url({ params: { id: ai.id() } }), {})
        .then((res) => {
          setMeetingActionItems([...meetingActionItems, res.data[0].id]);
          onActionItemToggle(meetingResource.id(), true);
          if (clientAssigned) {
            const updatedActionItems = clientActionItems.map(
              r => (r.id() === res.data[0].id ? fr.resource(res.data[0]) : r),
            );
            setClientActionItems(updatedActionItems);
          } else {
            const updatedActionItems = plannerActionItems.map(
              r => (r.id() === res.data[0].id ? fr.resource(res.data[0]) : r),
            );
            setPlannerActionItems(updatedActionItems);
          }
        });
    }
  };

  const [agenda, ...remainingAgendas] = meetingResource?.attr('agendas')
    .resources()
    .list()
    .filter(ag => ag.attr('sentTime'))
    .sort((a, b) => new Date(b.attr('sentTime')) - new Date(a.attr('sentTime')));

  return (
    <Paper
      elevation={0}
      square
      className={classNames(classes.root, className)}
    >
      <div className={classes.content}>
        <Typography
          className={classes.titleTop}
          color="primary"
          variant="h3"
        >
            Agenda
        </Typography>
        <Expandable>
          <RichTextDisplay content={agenda?.attr('content')} />
        </Expandable>
        <Typography
          className={classes.titleTop}
          color="primary"
          variant="h3"
        >
            Sent Emails
        </Typography>
        <div className={classes.previousEmails}>
          {remainingAgendas.map(ag => <PreviousEmail key={ag.id()} agenda={ag} />)}
        </div>
        <div className={classes.actions}>
          <div className={classes.actionItems}>
            <Typography
              className={classes.titleTop}
              color="primary"
              variant="h3"
            >
                Tasks
            </Typography>
            <ActionItemSelector
              className={classes.actionItemList}
              actionItems={clientActionItems}
              addButtonText="Add Client Task"
              onAdd={() => {
                openModal(
                  buildModalQuery(modalConsts.simpleActionItemModal, {
                    defaultValues: {
                      priority: 'medium',
                      dueDate: moment.utc().add(1, 'month').toDate(),
                      assignedTo: 'Household',
                    },
                  }),
                );
              }}
              onClick={handleClickActionItem}
              onToggle={ai => handleToggleActionItem(ai, true)}
              selectedActionItemIds={meetingActionItems}
              title="Client"
              loading={false}
            />
            <ActionItemSelector
              className={classes.actionItemList}
              actionItems={plannerActionItems}
              addButtonText="Add Facet Task"
              onToggle={ai => handleToggleActionItem(ai, true)}
              onAdd={() => {
                openModal(
                  buildModalQuery(modalConsts.simpleActionItemModal, {
                    defaultValues: {
                      priority: 'medium',
                      dueDate: moment.utc().add(1, 'month').toDate(),
                      assignedTo: 'Me',
                    },
                  }),
                );
              }}
              onClick={handleClickActionItem}
              selectedActionItemIds={meetingActionItems}
              title="Facet"
              loading={false}
              showBookmarkLegend
            />
          </div>
          <Divider flexItem orientation="vertical" light />
          <div className={classes.messages}>
            <Typography
              className={classes.titleTop}
              color="primary"
              variant="h3"
            >
                Discussion
            </Typography>
            <Messages
              meeting={meetingResource}
            />
          </div>
        </div>
      </div>
      <div className={classes.actionButtonContainer}>
        <Button
          disableElevation
          className={classes.openMissionControl}
          variant="contained"
          fullWidth
          color="secondary"
          component="a"
          href={missionControlURL(meetingResource, meetingResource.attr('householdId'), { meetingSelfLink: meetingResource.link('self').url() })}
        >
          Mission Control
        </Button>
        <Button
          disableElevation
          className={classes.openMissionControl}
          variant="contained"
          fullWidth
          color="secondary"
          component="a"
          href={householdBlueprintUri(meetingResource.attr('householdId'))}
        >
          Blueprint
        </Button>
        <Button
          disableElevation
          className={classes.openMissionControl}
          variant="contained"
          fullWidth
          color="secondary"
          component="a"
          href={householdPlansUri(meetingResource.attr('householdId'))}
        >
          Plans
        </Button>
      </div>
    </Paper>
  );
}

Meeting.defaultProps = {
  className: '',
  meetingResource: null,
};

Meeting.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  plannerActionItems: PropTypes.object.isRequired,
  clientActionItems: PropTypes.object.isRequired,
  meetingResource: PropTypes.object,
  setClientActionItems: PropTypes.func.isRequired,
  setPlannerActionItems: PropTypes.func.isRequired,
  meetingActionItems: PropTypes.object.isRequired,
  setMeetingActionItems: PropTypes.object.isRequired,
  onActionItemToggle: PropTypes.func.isRequired,
};

export default withStyles(styles)(Meeting);
